import React, {forwardRef, useContext, useImperativeHandle, useState} from "react";
import {Button, Flex, Result, Spin, Upload, UploadProps} from "antd";
import {LoadingOutlined, UploadOutlined, CheckCircleOutlined} from "@ant-design/icons";
import {StatusContext} from "../../../services/types";
import {StatusProps, LoadingStatus} from "./types";
import { uploadRightBook } from "../../../services/api";

interface RightStatus {
    loadingStatus: LoadingStatus;
    error?: string;
    rightName?: string;
}

export interface RightUploadHandle {
    uploadExampleFile: (
        file: File,
        projectID: string
    ) => Promise<{
        right_book_name: string;
        left_chapters: Array<{ id: string; name: string }>;
        right_chapters: Array<{ id: string; name: string }>;
    }>;
}

const RightUpload = forwardRef<RightUploadHandle, StatusProps>((rightStatusProps: StatusProps, ref) => {
    const [rightStatus, setRightStatus] = useState<RightStatus>({
        loadingStatus: LoadingStatus.Init,
    });

    const statusContext = useContext(StatusContext);
    if (!statusContext) {
        throw new Error("missing context");
    }

    const { status } = statusContext;

    const performUpload = async (file: File, projectIDOverride?: string) => {
        setRightStatus({
            loadingStatus: LoadingStatus.Loading,
        });

        const realProjectID = projectIDOverride || status.projectID;

        if (!realProjectID) {
            const error = new Error("Project ID is missing");
            setRightStatus({
                loadingStatus: LoadingStatus.Fail,
                error: error.message,
            });
            rightStatusProps.setResult(false, null, error);
            throw error;
        }

        try {
            const data = await uploadRightBook(realProjectID, file);
            setRightStatus({
                loadingStatus: LoadingStatus.Success,
                rightName: data.right_book_name,
            });
            rightStatusProps.setResult(true, {
                nextEnabled: true,
                rightName: data.right_book_name,
                leftChapters: data.left_chapters.map((cpt: { id: string; name: string }) => ({
                    id: cpt.id,
                    name: cpt.name,
                    skipped: false,
                })),
                rightChapters: data.right_chapters.map((cpt: { id: string; name: string }) => ({
                    id: cpt.id,
                    name: cpt.name,
                    skipped: false,
                })),
            }, null);
            return data;
        } catch (error: any) {
            setRightStatus({
                loadingStatus: LoadingStatus.Fail,
                error: error?.message || 'Upload failed',
            });
            rightStatusProps.setResult(false, null, error);
            throw error;
        }
    };

    const props: UploadProps = {
        name: 'right',
        customRequest: async (componentsData) => {
            try {
                const file = componentsData.file as File;
                await performUpload(file);
                componentsData.onSuccess?.({}, file as any);
            } catch (error) {
                componentsData.onError?.(error as Error);
            }
        },
    };

    useImperativeHandle(ref, () => ({
        uploadExampleFile: (file: File, projectID: string) => performUpload(file, projectID),
    }));

    return (
        <>
            {
                rightStatus.loadingStatus === LoadingStatus.Init ?
                    <Flex justify="center" align="center" style={{ height: '100%' }}>
                        <Upload {...props}>
                            <Button icon={<UploadOutlined />}>Select a book that will be right</Button>
                        </Upload>
                    </Flex>: null
            }
            {
                rightStatus.loadingStatus === LoadingStatus.Loading ?
                    <Spin indicator={<LoadingOutlined spin />} size="large"/> : null
            }
            {
                rightStatus.loadingStatus === LoadingStatus.Success ?
                    <Result status="success" title={rightStatus.rightName} icon={<CheckCircleOutlined style={{color: 'rgba(43, 43, 43, 1)'}} />} /> : null
            }
            {
                rightStatus.loadingStatus === LoadingStatus.Fail ?
                    <Result status="error" title={rightStatus.error}/> : null
            }
        </>
    )
});

RightUpload.displayName = 'RightUpload';

export default RightUpload;